import { useCallback, useMemo, useState } from 'react';

import { AgGridReact } from 'ag-grid-react';

import { Capitalize, formatLargeNumber } from 'lib/helpers/utility';
import { useLocationImportData } from 'lib/hooks/queries/admin/useLocationImportData';

import Avatar from 'components/shared/Avatar/Avatar';
import Button from 'components/shared/Button/Button';
import CustomPagination from 'components/shared/CustomPagination/CustomPagination';
import Filters from 'components/shared/Filters/Filters';
import Icon from 'components/shared/Icon/Icon';
import Modal from 'components/shared/Modal/Modal';
import Popover from 'components/shared/Popovers/Popover/Popover';
import Typography from 'components/shared/Typography/Typography';

const DEFAULT_FILTERS_DEFS = {
  name: {
    title: 'Name',
    values: null,
    allowed_operators: ['contains', 'does not contain'],
    icon: 'editor-text-mono'
  }
};

const LIMIT_OPTIONS = [
  { label: '10', value: 10 },
  { label: '25', value: 25 },
  { label: '50', value: 50 },
  { label: '100', value: 100 }
];

const EmptyState = () => (
  <div className="flex h-full w-full flex-col items-center justify-center py-12">
    <Icon icon="illustration-empty-table" />
    <Typography weight="semibold" className="mb-1 text-neutral-800">
      No items found
    </Typography>
    <Typography className="text-neutral-500">Try adjusting your search or filters.</Typography>
  </div>
);

const NameCellRenderer = ({ value }) => {
  if (!value) return <span>-</span>;

  const nameParts = value.split(' ');
  return (
    <div className="flex items-center gap-2">
      <Avatar firstName={nameParts[0]} lastName={nameParts[1]} size="small" />
      <span>{Capitalize(value)}</span>
    </div>
  );
};

const AddItemsModal = ({
  isOpen,
  onClose,
  onAdd,
  title,
  subtitle,
  practiceId,
  tableType,
  existingItems
}) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [gridApi, setGridApi] = useState(null);
  const [filters, setFilters] = useState({});

  const searchValue = filters?.name?.[0]?.values || '';

  const changeFilters = useCallback(
    (updater) => {
      setFilters((prevFilters) => {
        const currentFilters = prevFilters || {};
        const newFilters = typeof updater === 'function' ? updater(currentFilters) : updater;
        return newFilters || {};
      });
      setPage(1);
    },
    [setFilters, setPage]
  );

  const { data, isLoading } = useLocationImportData({
    params: {
      table: tableType,
      search: searchValue,
      practice_id: practiceId,
      page,
      limit,
      currentPracticeId: practiceId + ''
    },
    dependencies: [tableType, practiceId, page, limit, searchValue],
    options: { enabled: isOpen }
  });

  const items = data?.tableData || [];
  const totalItems = data?.count || items.length;
  const existingIds = new Set(existingItems.map((item) => item.id));
  const availableItems = items.filter((item) => !existingIds.has(item.id));

  const columnDefs = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        flex: 1,
        headerCheckboxSelection: true,
        checkboxSelection: true,
        showDisabledCheckboxes: true
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        cellRenderer: NameCellRenderer
      }
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true
    }),
    []
  );

  const onGridReady = useCallback((params) => {
    setGridApi(params.api);
  }, []);

  const onSelectionChanged = useCallback(() => {
    if (gridApi) {
      const selected = gridApi.getSelectedRows();
      setSelectedItems(selected);
    }
  }, [gridApi]);

  const handleAdd = () => {
    onAdd(selectedItems);
    setSelectedItems([]);
  };

  const handleClose = () => {
    setSelectedItems([]);
    setPage(1);
    onClose();
  };

  const start = totalItems > 0 ? (page - 1) * limit + 1 : 0;
  const end = Math.min(page * limit, totalItems);

  return (
    <Modal
      isOpen={isOpen}
      handleClose={handleClose}
      title={Capitalize(title)}
      className="w-[700px]"
      footer={
        <div className="flex w-full items-center justify-between">
          <Typography className="text-sm text-neutral-600">
            {selectedItems.length} rows selected
          </Typography>
          <div className="flex gap-2">
            <Button
              text="Cancel"
              color="neutral"
              onClick={handleClose}
              style="outlined"
              size="small"
            />
            <Button
              text={`Add ${tableType}`}
              onClick={handleAdd}
              isDisabled={selectedItems.length === 0}
              size="small"
            />
          </div>
        </div>
      }>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <Typography weight="semibold" className="text-neutral-900">
            {Capitalize(tableType)}
          </Typography>
          <Typography className="text-sm text-neutral-600">{subtitle}</Typography>
        </div>

        <div className="flex flex-col overflow-hidden rounded-lg border border-solid border-neutral-200">
          <div className="relative z-20 flex items-center justify-between gap-2 border-0 border-b border-solid border-neutral-200 bg-white px-4 py-2">
            <Filters
              defaultFilters={DEFAULT_FILTERS_DEFS}
              filters={filters || {}}
              changeFilters={changeFilters}
              position="left"
            />
          </div>

          <div className="ag-theme-quartz h-[400px] [&_.ag-root-wrapper]:!rounded-none [&_.ag-root-wrapper]:!border-0">
            {isLoading ? (
              <div className="flex h-full items-center justify-center">
                <Typography className="text-neutral-500">Loading...</Typography>
              </div>
            ) : (
              <AgGridReact
                columnDefs={columnDefs}
                rowData={availableItems}
                defaultColDef={defaultColDef}
                onGridReady={onGridReady}
                onSelectionChanged={onSelectionChanged}
                getRowId={(params) => String(params.data.id)}
                rowSelection="multiple"
                noRowsOverlayComponent={EmptyState}
                suppressRowClickSelection={false}
                rowMultiSelectWithClick={true}
              />
            )}
          </div>

          <div className="flex items-center justify-between border-b-0 border-l-0 border-r-0 border-t border-solid border-neutral-200 bg-white px-4 py-2">
            <div className="flex items-center gap-1">
              <Typography variant="label" size="small" weight="medium" color="neutral-700">
                {formatLargeNumber(start)}
              </Typography>
              <Typography variant="label" size="small" color="neutral-500">
                to
              </Typography>
              <Typography variant="label" size="small" weight="medium" color="neutral-700">
                {formatLargeNumber(end)}
              </Typography>
              <Typography variant="label" size="small" color="neutral-500">
                of
              </Typography>
              <Typography variant="label" size="small" weight="medium" color="neutral-700">
                {formatLargeNumber(totalItems)}
              </Typography>
              <Popover
                customButtonTrigger={({ open }) => (
                  <Button
                    variant="tertiary"
                    size="small"
                    text={String(limit)}
                    iconRight="chevron-down-linear"
                    color="neutral"
                    iconSize={16}
                    className={`ml-2 h-6 w-fit bg-white py-0 pl-2 pr-1 ${open ? 'bg-neutral-100' : ''}`}
                  />
                )}
                optionIconColor="neutral"
                iconsShade={700}
                options={LIMIT_OPTIONS}
                onOptionClick={(option, close) => {
                  setLimit(option.value);
                  setPage(1);
                  close();
                }}
                textClassName="text-neutral-900"
                isFixed
                position="left"
              />
            </div>
            <CustomPagination
              page={page}
              perPage={limit}
              totalItems={totalItems}
              onPageChange={setPage}
              pageCountOverride={null}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AddItemsModal;
